import { useMemo, useState } from 'react';

import { EventMarket, EventResponse } from '@/types';
import { getCanonicalMarketLine, getMarketType } from '@/utils';

interface UseLineSelectionProps {
  event: EventResponse;
  marketType: string;
  initialMarket?: any;
  lineGroupMarkets?: EventMarket[];
  isML: boolean;
  isSpread: boolean;
  isTotal: boolean;
  spreadRailSelectsByMarketId: boolean;
  hasLineSlider?: boolean;
}

export const useLineSelection = ({
  event,
  marketType,
  initialMarket,
  lineGroupMarkets,
  isML,
  isSpread,
  isTotal,
  spreadRailSelectsByMarketId,
  hasLineSlider,
}: UseLineSelectionProps) => {
  const [userSelectedLine, setUserSelectedLine] = useState<string | number | undefined>(undefined);
  const [prevInitialId, setPrevInitialId] = useState<string | number | undefined>(
    initialMarket?.id,
  );

  if (initialMarket?.id !== prevInitialId) {
    setPrevInitialId(initialMarket?.id);
    setUserSelectedLine(undefined);
  }

  const allLines = useMemo(() => {
    if (isML) return [];
    if (lineGroupMarkets && lineGroupMarkets.length > 0) {
      if (spreadRailSelectsByMarketId) {
        return lineGroupMarkets.map((m) => String(m.id));
      }
      const lines = new Set<string | number>();
      lineGroupMarkets.forEach((m) => {
        const threshold = getCanonicalMarketLine(m);
        if (threshold != null && String(threshold).trim() !== '') lines.add(threshold);
      });
      return Array.from(lines).sort((a, b) => Number(a) - Number(b));
    }

    const lines = new Set<string | number>();
    event.markets?.forEach((m) => {
      const type = getMarketType(m);
      const threshold = getCanonicalMarketLine(m);
      if (threshold == null) return;
      if (
        (isSpread && type.includes('spread')) ||
        (isTotal && type.includes('total')) ||
        type === marketType.toLowerCase()
      ) {
        lines.add(threshold);
      }
    });
    return Array.from(lines).sort((a, b) => Number(a) - Number(b));
  }, [
    event.markets,
    isML,
    lineGroupMarkets,
    spreadRailSelectsByMarketId,
    isSpread,
    isTotal,
    marketType,
  ]);

  const selectedLine = useMemo(() => {
    if (userSelectedLine !== undefined) return userSelectedLine;
    if (allLines.length === 0) return undefined;

    if (spreadRailSelectsByMarketId && lineGroupMarkets?.length) {
      const initId = String(initialMarket?.id ?? '');
      return allLines.includes(initId) ? initId : String(allLines[0]);
    }

    const initialCanon = getCanonicalMarketLine(initialMarket);
    if (initialCanon != null && allLines.some((l) => String(l) === String(initialCanon))) {
      return initialCanon;
    }

    return allLines[0];
  }, [userSelectedLine, allLines, initialMarket, spreadRailSelectsByMarketId, lineGroupMarkets]);

  const activeMarket = useMemo(() => {
    if (isML) return initialMarket || event.markets?.find((m) => getMarketType(m) === 'moneyline');
    if (selectedLine == null) return initialMarket;

    if (lineGroupMarkets?.length) {
      if (spreadRailSelectsByMarketId) {
        return lineGroupMarkets.find((m) => String(m.id) === String(selectedLine));
      }
      return lineGroupMarkets.find(
        (m) => String(getCanonicalMarketLine(m)) === String(selectedLine),
      );
    }

    return event.markets?.find((m) => {
      const type = getMarketType(m);
      const isCorrectType =
        (isSpread && type.includes('spread')) ||
        (isTotal && type.includes('total')) ||
        type === marketType.toLowerCase();
      return isCorrectType && String(getCanonicalMarketLine(m)) === String(selectedLine);
    });
  }, [
    selectedLine,
    isML,
    initialMarket,
    event.markets,
    lineGroupMarkets,
    spreadRailSelectsByMarketId,
    isSpread,
    isTotal,
    marketType,
  ]);

  const showLineRail = !isML && (allLines.length > 1 || (hasLineSlider && allLines.length > 0));

  return { allLines, selectedLine, activeMarket, showLineRail, setUserSelectedLine };
};
